import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, Edit3, Printer, Lock, AlertTriangle, X, Save, Copy, CheckCircle2, FlaskConical, Plus } from "lucide-react";
import { listProfiles, getLatest, updateProfile, createProfile } from "@/lib/profiles/storage";
import { listRceCandidates, isRceCandidateId, materializeRceCandidate } from "@/lib/profiles/rceBridge";
import { VALIDATORS, PROFILE_KIND_LABELS, EMPTY_FACTORIES } from "@/lib/profiles/schemas";
import { newSession, attachProfile, saveDraft, loadDraft, saveKillSheet, cloneSession, createKillSheetFromSession, freezeDrillPipeGeometry } from "@/lib/killsheet/sessionStore";
import { composeKillSheet } from "@/lib/killsheet/composer";
import { validateSession } from "@/lib/killsheet/validate";
import { deriveSession, hasAnyOverride } from "@/lib/killsheet/autoDerive";
import { describeDrillPipeSource } from "@/lib/killsheet/drillPipeResolver";
import { isProActive } from "@/lib/featureAccess";
import { getCachedEntitlement } from "@/lib/entitlement";
import SubscribeModal from "@/components/SubscribeModal";
import PremiumResultsLock from "@/components/PremiumResultsLock";
import { pumpCalibration } from "@/lib/calc/pumpDisplacement";
import { ONBOARDING_ORDER, countBound, nextUnboundKind, KIND_DISPLAY_LABEL } from "@/lib/killsheet/onboarding";

import WellProfileForm   from "@/pages/profiles/forms/WellProfileForm";
import BHAProfileForm    from "@/pages/profiles/forms/BHAProfileForm";
import CasingProfileForm from "@/pages/profiles/forms/CasingProfileForm";
import MudProfileForm    from "@/pages/profiles/forms/MudProfileForm";
import PumpProfileForm   from "@/pages/profiles/forms/PumpProfileForm";
import OpStateProfileForm from "@/pages/profiles/forms/OperationalStateProfileForm";

const FORMS = {
  well:    WellProfileForm,
  bha:     BHAProfileForm,
  casing:  CasingProfileForm,
  mud:     MudProfileForm,
  pump:    PumpProfileForm,
  opstate: OpStateProfileForm,
};

// Phase 1 feature flag — flip to `false` to fully restore the pre-Phase-1
// behavior (new-tab profile creation, no onboarding strip). Kept here so
// rollback is a single-line constant change. See UX-1 architecture review.
const INLINE_PROFILE_CREATE_ENABLED = true;

const VARIANTS = {
  "surface-vertical":   "Surface BOP · Vertical Well",
  "surface-horizontal": "Surface BOP · Horizontal Well",
  "subsea-vertical":    "Subsea BOP · Vertical Well",
  "subsea-horizontal":  "Subsea BOP · Horizontal Well",
};
const KINDS_ON_EDITOR = ["well", "bha", "casing", "mud", "pump", "opstate"];

export default function KillSheetEditor() {
  const { variant } = useParams();
  const [session, setSession] = useState(() => loadDraft() || newSession(variant));
  const [overrides, setOverrides] = useState({});
  // Quick-Edit drawer state ----------------------------------------------------
  const [drawer, setDrawer] = useState(null);   // { kind, id, draft } | null
  const [drawerErrors, setDrawerErrors] = useState([]);
  const [rebindPrompt, setRebindPrompt] = useState(null); // { kind, id, version, label }
  // Phase 1 — inline profile creation state. Single-active accordion to avoid
  // layout sprawl. Null = no creator open.
  const [creatingKind, setCreatingKind] = useState(null);
  const pro = isProActive(getCachedEntitlement());
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  useEffect(() => { saveDraft(session); }, [session]);
  useEffect(() => {
    if (variant && session.variant !== variant) {
      setSession(newSession(variant)); setOverrides({});
    }
  }, [variant]);

  // Compose-ready session: merge derived lengths/capacities into the session
  // BEFORE handing to composeKillSheet — composer remains contract-stable.
  const derived = useMemo(() => deriveSession(session, overrides), [session, overrides]);
  const sessionForCompose = useMemo(() => {
    // Apply OpState→Well fallback chain into a shadow session so composer
    // (which reads session.well.currentMd etc.) gets the correct values
    // without changing the composer contract.
    const shadow = JSON.parse(JSON.stringify(session));
    if (shadow.opstate) {
      shadow.well = shadow.well || {};
      shadow.mud  = shadow.mud  || {};
      if (shadow.opstate.currentMd)          shadow.well.currentMd          = shadow.opstate.currentMd;
      if (shadow.opstate.currentTvd)         shadow.well.currentTvd         = shadow.opstate.currentTvd;
      if (shadow.opstate.holeSize)           shadow.well.holeSize           = shadow.opstate.holeSize;
      if (shadow.opstate.currentMudDensity)  shadow.mud.density             = shadow.opstate.currentMudDensity;
      if (shadow.opstate.currentMudGradient) shadow.mud.gradient            = shadow.opstate.currentMudGradient;
      if (shadow.opstate.activeSurfaceVolume) shadow.mud.activeSurfaceVolume = shadow.opstate.activeSurfaceVolume;
    }
    // Map OpState pump operational values into the bound pump profile
    if (shadow.opstate && shadow.pump?.pumps?.length) {
      const idx = shadow.pump.selectedIndex ?? 0;
      const p = shadow.pump.pumps[idx];
      if (p) {
        if (shadow.opstate.currentPumpDisplacement && !p.displacement)
          p.displacement = shadow.opstate.currentPumpDisplacement;
        if (shadow.opstate.currentPumpSpm && !p.spm)
          p.spm = shadow.opstate.currentPumpSpm;
        if (shadow.opstate.currentSlowPumpPressure && !p.slowPumpPressure)
          p.slowPumpPressure = shadow.opstate.currentSlowPumpPressure;
      }
    }
    shadow.lengths    = { ...shadow.lengths,    ...derived.composerInput.lengths };
    shadow.capacities = { ...shadow.capacities, ...derived.composerInput.capacities };
    return shadow;
  }, [session, derived]);

  const validation = useMemo(() => validateSession(sessionForCompose), [sessionForCompose]);
  const composed   = useMemo(() => composeKillSheet(sessionForCompose),  [sessionForCompose]);

  const pickProfile = (kind, id) => {
    if (!id) return;
    try {
      // A picker row can offer live Engineering Setup candidates (see
      // PickerRow below) alongside legacy profiles -- materialize the pick
      // into a real legacy profile first (same path the "+" inline creator
      // and CreateKillSheetFromOpStateModal already use) before attaching.
      const realId = isRceCandidateId(id)
        ? materializeRceCandidate(kind, id).id
        : id;
      const next = attachProfile(JSON.parse(JSON.stringify(session)), kind, realId);
      setSession(next);
    } catch (e) { alert(e.message); }
  };

  // ---- Phase 1 inline profile creation handlers ----
  const handleToggleCreate = (kind) => {
    setCreatingKind((cur) => (cur === kind ? null : kind));
  };
  const handleCancelCreate = () => setCreatingKind(null);
  const handleCreatedProfile = (kind, newId) => {
    // Same code path as pickProfile — embeds the snapshot via attachProfile.
    try {
      const next = attachProfile(JSON.parse(JSON.stringify(session)), kind, newId);
      setSession(next);
    } catch (e) { alert(e.message); }
    setCreatingKind(null);
  };

  // ---- Quick-Edit drawer handlers ----
  const openDrawer = (kind, id) => {
    if (!id) return;
    const row = getLatest(kind, id);
    if (!row) { alert("Profile not found"); return; }
    setDrawer({ kind, id, draft: JSON.parse(JSON.stringify(row.payload)), label: row.label, version: row.version });
    setDrawerErrors([]);
  };
  const closeDrawer = () => { setDrawer(null); setDrawerErrors([]); };
  const saveDrawer = () => {
    if (!drawer) return;
    const { kind, id, draft: payload } = drawer;
    const result = VALIDATORS[kind](payload);
    if (!result.ok) { setDrawerErrors(result.errors || []); return; }
    try {
      const updated = updateProfile(kind, id, payload);
      // Prompt the user before we re-bind the snapshot to the new version.
      // User selected option 4b: explicit confirm — keep snapshot OR re-bind.
      setRebindPrompt({
        kind, id,
        version: updated.version,
        label: updated.label || payload.label || "(unnamed)",
      });
      setDrawer(null);
      setDrawerErrors([]);
    } catch (e) {
      setDrawerErrors(e.validationErrors || [e.message || "Save failed"]);
    }
  };
  const confirmRebind = () => {
    if (!rebindPrompt) return;
    const { kind, id } = rebindPrompt;
    try {
      const next = attachProfile(JSON.parse(JSON.stringify(session)), kind, id);
      setSession(next);
    } catch (e) { alert(e.message); }
    setRebindPrompt(null);
  };
  const dismissRebind = () => setRebindPrompt(null);

  const setKick = (field, val) =>
    setSession({ ...session, kickData: { ...session.kickData, [field]: val } });

  const resetOverride = (key) => {
    const next = { ...overrides }; delete next[key]; setOverrides(next);
  };
  const resetAllOverrides = () => setOverrides({});

  const o = composed?.ok ? composed.output : null;
  const setUnits = (u) => setSession({ ...session, units: u });

  // ---- Clone (P2 v5) — secondary action on a CREATED sheet ----
  // Kill Sheet workflow correction (ticket 255586): Clone is no longer the
  // header's primary action. It is offered only once this sheet has been
  // created, it confirms first, and it never touches THIS sheet — the copy
  // is a new scenario with cleared kick data and live Drill Pipe geometry.
  const handleClone = () => {
    if (!session._createdAt) return;
    const ok = window.confirm(
      "Clone this Kill Sheet into a new scenario?\n\nThe copy keeps every bound profile, starts with empty kick data (SIDPP / SICP / Pit Gain) and resolves Drill Pipe geometry from the current Engineering Setup. This sheet is not changed.",
    );
    if (!ok) return;
    const cloned = cloneSession(session);
    saveDraft(cloned);
    // Force a remount of the editor with the new draft. We can't navigate
    // because the URL is variant-keyed, not session-id-keyed. Trigger a
    // state refresh via setSession(loadDraft()).
    setSession(cloned);
    setOverrides({});
    // Scroll to the kick-data input so the supervisor starts the new scenario.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="ks-section-kick-data-(the-only-fields-you-type)"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  // ---- Create Kill Sheet (workflow correction, ticket 255586) ----
  // The explicit primary action once the sheet is valid: freezes the Drill
  // Pipe geometry resolved from the canonical Engineering Setup and stamps
  // the creation time. Outputs are already live; nothing is recomputed
  // differently — the sheet simply stops following later Drill String edits.
  const handleCreateKillSheet = () => {
    if (!composed?.ok) return;
    const next = createKillSheetFromSession(JSON.parse(JSON.stringify(session)));
    setSession(next);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="ks-section-results-(live)"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  // ---- Next-step guidance (P3 v5) ----
  // Calm guidance card when kick data is empty AND the session has a ready
  // set of profile bindings. The former "Begin Kick Entry" button was removed
  // (ticket 255586): SIDPP / SICP / Pit Gain sit directly below this card.
  const kickEmpty = !session.kickData?.sidpp && !session.kickData?.sicp && !session.kickData?.pitGain;
  const profilesBound = ["well", "bha", "casing", "mud", "pump"].every((k) => session[k]);
  const showNextStep = kickEmpty && profilesBound;

  return (
    <div data-testid="killsheet-editor" className="max-w-5xl mx-auto px-4 py-6">
      <Link to="/reports" className="inline-flex items-center gap-1 text-[11px] uppercase font-mono tracking-wider text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] mb-2">
        <ArrowLeft className="w-3 h-3" /> Reports
      </Link>
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[var(--rs-text-3)]">IWCF Kill Sheet</div>
          <h1 className="text-[20px] font-semibold tracking-tight text-[var(--rs-text-1)]">{VARIANTS[variant] || "Kill Sheet"}</h1>
        </div>
        {/* Clone moved out of the header (Kill Sheet workflow correction, ticket
            255586): it is a secondary action on a CREATED sheet — see the
            Create row below the Kick Data section. */}
        {session._createdAt && (
          <span data-testid="ks-created-pill"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded whitespace-nowrap"
                style={{ color: "#22C55E", border: "1px solid rgba(34,197,94,0.45)", background: "rgba(34,197,94,0.08)" }}>
            <CheckCircle2 className="w-3 h-3" /> Created
          </span>
        )}
      </header>

      {/* Persistent Kill Sheet Summary bar (user req) */}
      <SummaryBar o={o} session={sessionForCompose} units={session.units} onUnits={setUnits} />

      {session._clonedFrom && (
        <div data-testid="ks-cloned-from"
             className="card-rs p-2 mb-3 text-[10.5px]"
             style={{ borderColor: "rgba(148,163,184,0.45)", background: "rgba(148,163,184,0.06)" }}>
          <span className="text-[var(--rs-text-2)] font-mono uppercase tracking-[0.2em] mr-2">Cloned from</span>
          <span className="text-[var(--rs-text-1)]">
            <strong>{session._clonedFrom.label}</strong>
          </span>
          <span className="text-[var(--rs-text-3)] ml-2">
            (kick data cleared · {new Date(session._clonedFrom.at).toLocaleString()})
          </span>
        </div>
      )}

      {session._originOpState && (
        <div data-testid="ks-origin-opstate"
             className="card-rs p-2 mb-3 text-[10.5px]"
             style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}>
          <span className="text-[var(--rs-amber)] font-mono uppercase tracking-[0.2em] mr-2">Origin OpState</span>
          <span className="text-[var(--rs-text-1)]">
            <strong>{session._originOpState.label}</strong> · v{session._originOpState.version}
          </span>
          <span className="text-[var(--rs-text-3)] ml-2">
            (one-tap workflow · created {new Date(session._originOpState.at).toLocaleString()})
          </span>
        </div>
      )}

      {/* Soft warnings (e.g. unit-system mismatch with a bound profile) */}
      {validation.warnings && validation.warnings.length > 0 && (
        <div data-testid="ks-warnings" className="card-rs p-2 mb-3 text-[11px]"
             style={{ borderColor: "rgba(245,180,0,0.5)", background: "rgba(245,180,0,0.06)" }}>
          <div className="font-semibold text-[var(--rs-amber)] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Warnings
          </div>
          <ul className="list-disc list-inside text-[var(--rs-text-1)]">
            {validation.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
          </ul>
        </div>
      )}

      {/* Phase 1.5 — Onboarding strip. Renders only while bound < 6 AND the
          inline-create flag is on. Zero impact when bound === 6 or flag off. */}
      {INLINE_PROFILE_CREATE_ENABLED && (
        <OnboardingStrip session={session}
                         onCreateNext={(k) => setCreatingKind(k)} />
      )}

      {/* Profile pickers + Edit drawer triggers */}
      <Section title="Profiles">
        <div className="grid grid-cols-1 gap-2">
          {KINDS_ON_EDITOR.map((k) => (
            <PickerRow key={k} kind={k} session={session}
                       inlineEnabled={INLINE_PROFILE_CREATE_ENABLED}
                       expanded={creatingKind === k}
                       onToggleCreate={() => handleToggleCreate(k)}
                       onCancelCreate={handleCancelCreate}
                       onCreatedProfile={(id) => handleCreatedProfile(k, id)}
                       onPick={(id) => pickProfile(k, id)}
                       onEdit={(id) => openDrawer(k, id)} />
          ))}
        </div>
        <PumpCalibrationBanner session={session} />
      </Section>

      {/* Auto-populated data */}
      <Section title="Auto-populated data" right={
        hasAnyOverride(derived) ? (
          <button data-testid="ks-reset-all-overrides" onClick={resetAllOverrides}
            className="text-[10px] font-mono uppercase text-[var(--rs-amber)] inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Reset all overrides
          </button>
        ) : null
      }>
        <DerivedTable derived={derived} overrides={overrides} setOverrides={setOverrides}
                      onReset={resetOverride} units={session.units} />
      </Section>

      {/* Kick Data */}
      {showNextStep && (
        <div data-testid="ks-next-step"
             className="card-rs p-3 mb-3 flex items-center justify-between gap-3"
             style={{ borderColor: "rgba(245,180,0,0.5)", background: "rgba(245,180,0,0.05)" }}>
          <div>
            <div className="text-[9.5px] font-mono uppercase tracking-[0.25em] text-[var(--rs-amber)]">
              Next step
            </div>
            <div className="text-[13px] font-semibold text-[var(--rs-text-1)] mt-0.5">
              Enter Kick Data
            </div>
            <p className="text-[10.5px] text-[var(--rs-text-3)] mt-1 leading-snug">
              All profiles are bound and auto-derived data is ready. SIDPP, SICP
              and pit gain are the only values left to enter before the IWCF
              outputs finalise.
            </p>
          </div>
        </div>
      )}

      <Section title="Kick Data (the only fields you type)">
        <div className="grid grid-cols-3 gap-2">
          <KickInput label="SIDPP"   field="sidpp"   value={session.kickData.sidpp}   onChange={setKick} testid="ks-kick-sidpp" />
          <KickInput label="SICP"    field="sicp"    value={session.kickData.sicp}    onChange={setKick} testid="ks-kick-sicp" />
          <KickInput label="Pit Gain" field="pitGain" value={session.kickData.pitGain} onChange={setKick} testid="ks-kick-pitGain" />
        </div>
        {!validation.ok && (
          <div data-testid="ks-validation" className="card-rs p-2 mt-2 text-[11px]"
               style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
            <div className="font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Cannot compute — fix:
            </div>
            <ul className="list-disc list-inside text-[var(--rs-text-1)]">
              {validation.errors.map((e, i) => <li key={i}>{e.message}</li>)}
            </ul>
          </div>
        )}
      </Section>

      {/* Create Kill Sheet — primary action (workflow correction, ticket 255586) */}
      <div data-testid="ks-create-row"
           className="card-rs p-3 mb-3 flex items-center justify-between gap-3 flex-wrap"
           style={session._createdAt
             ? { borderColor: "rgba(34,197,94,0.45)", background: "rgba(34,197,94,0.05)" }
             : { borderColor: "rgba(245,180,0,0.5)", background: "rgba(245,180,0,0.05)" }}>
        <div className="min-w-0">
          <div className="text-[9.5px] font-mono uppercase tracking-[0.25em]"
               style={{ color: session._createdAt ? "#22C55E" : "var(--rs-amber)" }}>
            {session._createdAt ? "Kill Sheet created" : "Final step"}
          </div>
          <div className="text-[13px] font-semibold text-[var(--rs-text-1)] mt-0.5">
            {session._createdAt
              ? `Created ${new Date(session._createdAt).toLocaleString()}`
              : composed?.ok ? "Create the Kill Sheet" : "Enter SIDPP, SICP and Pit Gain to create the Kill Sheet"}
          </div>
          <p className="text-[10.5px] text-[var(--rs-text-3)] mt-1 leading-snug">
            {session._createdAt
              ? `Drill Pipe geometry is frozen for this sheet (${describeDrillPipeSource(session.drillPipe)}). Kick data and results stay editable; clone to start a new scenario.`
              : "Creating locks the Drill Pipe geometry resolved from your Engineering Setup so this sheet stays reproducible. Results below are already live."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {session._createdAt && (
            <button type="button"
                    data-testid="ks-clone-btn"
                    onClick={handleClone}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium whitespace-nowrap"
                    style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
              <Copy className="w-4 h-4" /> Clone as new scenario
            </button>
          )}
          <button type="button"
                  data-testid="ks-create-btn"
                  disabled={!composed?.ok || !!session._createdAt}
                  onClick={handleCreateKillSheet}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium whitespace-nowrap disabled:opacity-40"
                  style={session._createdAt
                    ? { background: "transparent", color: "#22C55E", border: "1px solid rgba(34,197,94,0.45)", minHeight: 40 }
                    : { background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
            {session._createdAt ? <CheckCircle2 className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}
            {session._createdAt ? "Created" : "Create Kill Sheet"}
          </button>
        </div>
      </div>

      {/* Results — Pro-only computed output. Inputs above (SIDPP/SICP/Pit
          Gain, profiles, etc.) stay fully free/editable; only the derived
          kill sheet calculations are withheld here. */}
      {pro ? (
        <>
          <Section title="Results (live)">
            <ResultsGrid o={o} units={session.units} />
          </Section>

          <Section title="Step-down schedule & DPP graph">
            <StepDownAndGraph o={o} units={session.units} />
          </Section>
        </>
      ) : (
        <Section title="Results (live)">
          <PremiumResultsLock
            label="kill sheet calculations (KMD, ICP, FCP, MAASP, step-down schedule)"
            onUnlock={() => setUpgradeOpen(true)}
          />
        </Section>
      )}

      <ExportFooter session={sessionForCompose} composed={composed} />

      <SubscribeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      {drawer && (
        <QuickEditDrawer drawer={drawer}
                         errors={drawerErrors}
                         onChange={(d) => setDrawer({ ...drawer, draft: d })}
                         onClose={closeDrawer}
                         onSave={saveDrawer} />
      )}
      {rebindPrompt && (
        <RebindPrompt prompt={rebindPrompt}
                      onConfirm={confirmRebind}
                      onDismiss={dismissRebind} />
      )}
    </div>
  );
}

// ---- Sticky summary bar -----------------------------------------------------
function SummaryBar({ o, session, units, onUnits }) {
  const items = [
    ["Well",   o?.wellSnapshot?.wellName || session.well?.label || "—"],
    ["MD",     o?.wellSnapshot?.md ?? "—"],
    ["TVD",    o?.wellSnapshot?.tvd ?? "—"],
    ["MW",     o?.currentWell?.mudDensity?.display ?? session.mud?.density ?? "—"],
    ["SIDPP",  session.kickData?.sidpp || "—"],
    ["SICP",   session.kickData?.sicp || "—"],
    ["KMD",    o?.killCalc?.kmd?.display ?? "—"],
    ["ICP",    o?.killCalc?.icp?.display ?? "—"],
    ["FCP",    o?.killCalc?.fcp?.display ?? "—"],
  ];
  return (
    <div data-testid="ks-summary-bar"
         className="card-rs sticky top-0 z-20 mb-3 px-2 py-2"
         style={{ background: "rgba(10,11,14,0.92)", backdropFilter: "blur(6px)" }}>
      <div className="grid grid-cols-3 sm:grid-cols-9 gap-2">
        {items.map(([k, v]) => (
          <div key={k} data-testid={`ks-summary-${k.toLowerCase()}`} className="text-center">
            <div className="text-[9px] font-mono uppercase text-[var(--rs-text-3)] tracking-wider">{k}</div>
            <div className="text-[13px] font-mono text-[var(--rs-amber)]">{v}</div>
          </div>
        ))}
      </div>
      {onUnits && (
        <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-[var(--rs-border)]">
          <span className="text-[10px] font-mono uppercase text-[var(--rs-text-3)] tracking-wider">Units</span>
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: "1px solid var(--rs-border)" }}>
            <button data-testid="ks-units-imp" type="button" onClick={() => onUnits("IMP")}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider"
                    style={{
                      background: units === "IMP" ? "var(--rs-amber)" : "transparent",
                      color: units === "IMP" ? "#0A0B0E" : "var(--rs-text-2)",
                      minHeight: 26, minWidth: 56,
                    }}>Imperial</button>
            <button data-testid="ks-units-si" type="button" onClick={() => onUnits("SI")}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider"
                    style={{
                      background: units === "SI" ? "var(--rs-amber)" : "transparent",
                      color: units === "SI" ? "#0A0B0E" : "var(--rs-text-2)",
                      minHeight: 26, minWidth: 56,
                    }}>Metric</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pump calibration banner (P3) -----------------------------------------
// Read-only summary of the selected pump's measured-vs-derived variance.
// Renders only when the bound Pump profile has both measuredStrokes AND
// measuredVolume populated. Never blocks compose.
function PumpCalibrationBanner({ session }) {
  const pumps = session?.pump?.pumps || [];
  const idx = session?.pump?.selectedIndex ?? 0;
  const pump = pumps[idx];
  if (!pump) return null;
  if (!pump.measuredStrokes && !pump.measuredVolume) return null;
  const cal = pumpCalibration(pump, session.units);
  const status = cal?.status || null;
  const statusColor = status === "green" ? "#22C55E"
                    : status === "amber" ? "#F5B400"
                    : status === "red"   ? "#FCA5A5"
                    : "var(--rs-text-3)";
  const statusLabel = status === "green" ? "Calibration OK"
                    : status === "amber" ? "Marginal calibration"
                    : status === "red"   ? "Re-calibrate before kill"
                    : "Calibration data incomplete";
  return (
    <div data-testid="ks-pump-calibration-banner"
         className="card-rs mt-2 p-2 grid grid-cols-1 sm:grid-cols-5 items-center gap-2"
         style={{
           borderColor: `${statusColor}55`,
           background: `${statusColor}10`,
         }}>
      <div className="sm:col-span-2">
        <div className="text-[9px] font-mono uppercase tracking-[0.2em]" style={{ color: statusColor }}>
          Pump field calibration
        </div>
        <div className="text-[12px] font-semibold mt-0.5" style={{ color: statusColor }}>
          {statusLabel}
        </div>
      </div>
      <div className="text-center">
        <div className="text-[9px] font-mono uppercase text-[var(--rs-text-3)] tracking-wider">Derived</div>
        <div className="text-[12px] font-mono text-[var(--rs-text-1)]">
          {cal?.ok ? Number(cal.derived.toFixed(session.units === "SI" ? 6 : 4)) : "—"}
        </div>
      </div>
      <div className="text-center">
        <div className="text-[9px] font-mono uppercase text-[var(--rs-text-3)] tracking-wider">Actual</div>
        <div className="text-[12px] font-mono" style={{ color: statusColor }}>
          {cal?.ok ? Number(cal.actual.toFixed(session.units === "SI" ? 6 : 4)) : "—"}
        </div>
      </div>
      <div className="text-center">
        <div className="text-[9px] font-mono uppercase text-[var(--rs-text-3)] tracking-wider">Variance</div>
        <div className="text-[14px] font-mono font-semibold" style={{ color: statusColor }}>
          {cal?.ok && Number.isFinite(cal.variancePct)
            ? `${cal.variancePct > 0 ? "+" : ""}${cal.variancePct.toFixed(2)} %`
            : "—"}
        </div>
      </div>
    </div>
  );
}


function PickerRow({
  kind, session, onPick, onEdit,
  inlineEnabled, expanded, onToggleCreate, onCancelCreate, onCreatedProfile,
}) {
  // Live Engineering Setup candidates (see lib/profiles/rceBridge.js) come
  // first so a well/BHA/mud/pump set up the canonical way -- not through
  // the legacy Profile Manager -- is still selectable here, matching what
  // CreateKillSheetFromOpStateModal already offers.
  const rows = [...listRceCandidates(kind), ...listProfiles(kind)];
  const cur = session.refs?.[kind];
  return (
    <div className="card-rs p-2"
         data-testid={`ks-picker-${kind}`}>
      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-3 text-[11px] uppercase font-mono text-[var(--rs-text-3)]">{kind}</div>
        <select data-testid={`ks-pick-${kind}`} value={cur?.id || ""} onChange={(e) => onPick(e.target.value)}
                className="input-rs col-span-6 text-[12px]" style={{ minHeight: 32 }}>
          <option value="">Select…</option>
          {rows.map((r) => <option key={r.id} value={r.id}>{r.label} · v{r.version}</option>)}
        </select>
        {inlineEnabled ? (
          <button data-testid={`ks-new-${kind}`} type="button"
                  onClick={onToggleCreate}
                  aria-expanded={expanded ? "true" : "false"}
                  aria-controls={`ks-inline-creator-${kind}`}
                  className="col-span-1 inline-flex items-center justify-center text-[11px] rounded border border-[var(--rs-border)]"
                  style={{
                    minHeight: 32,
                    color: expanded ? "#0A0B0E" : "var(--rs-amber)",
                    background: expanded ? "var(--rs-amber)" : "transparent",
                  }}
                  title={expanded ? "Close" : "Create new"}>
            {expanded ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          </button>
        ) : (
          <Link data-testid={`ks-new-${kind}`} to={`/profiles/${kind}/new`} target="_blank"
                className="col-span-1 text-[11px] text-center rounded border border-[var(--rs-border)] text-[var(--rs-amber)]"
                style={{ minHeight: 32, lineHeight: "30px" }}>+ New</Link>
        )}
        <button data-testid={`ks-edit-${kind}`}
                type="button"
                disabled={!cur?.id}
                onClick={() => onEdit && onEdit(cur?.id)}
                className="col-span-2 inline-flex items-center justify-center gap-1 text-[11px] rounded border border-[var(--rs-border)] text-[var(--rs-text-2)] disabled:opacity-30 hover:text-[var(--rs-amber)]"
                style={{ minHeight: 32 }}>
          <Edit3 className="w-3 h-3" /> Edit
        </button>
      </div>
      {inlineEnabled && expanded && (
        <InlineProfileCreator id={`ks-inline-creator-${kind}`}
                              kind={kind}
                              onCancel={onCancelCreate}
                              onSaved={onCreatedProfile} />
      )}
    </div>
  );
}

function SectionHeader({ title, testid }) {
  return (
    <div data-testid={testid}
         className="col-span-1 sm:col-span-2 text-[9px] font-mono uppercase tracking-[0.2em] text-[var(--rs-amber)] mt-3 mb-1 pt-2"
         style={{ borderTop: "1px solid var(--rs-border)" }}>
      {title}
    </div>
  );
}

function ContextRow({ label, value, unit, testid }) {
  // Same boxed input styling as DerivedRow below, for visual consistency
  // across the whole panel -- this field just stays read-only (it's a
  // value sourced from the bound profiles, not a Kill Sheet override).
  const display = value != null ? `${value}${unit ? ` ${unit}` : ""}` : "—";
  return (
    <div data-testid={testid} className="grid grid-cols-5 gap-1 items-center text-[11px]">
      <span className="col-span-2 text-[var(--rs-text-3)]">{label}</span>
      <input data-testid={`${testid}-input`} type="text" readOnly disabled
             value={display}
             className="input-rs col-span-2 font-mono text-[11px]"
             style={{ minHeight: 28, opacity: 0.75, cursor: "default" }} />
      <span className="col-span-1" />
    </div>
  );
}

function DerivedRow({ label, k, cell, overrides, setOverrides, onReset }) {
  return (
    <div data-testid={`ks-derived-${k}`} className="grid grid-cols-5 gap-1 items-center text-[11px]">
      <span className="col-span-2 text-[var(--rs-text-3)]">{label}</span>
      <input data-testid={`ks-derived-${k}-input`} type="number" step="0.0001"
             value={overrides[k] ?? (cell.value != null ? Number(cell.value.toFixed ? cell.value.toFixed(4) : cell.value) : "")}
             placeholder={cell.value != null ? String(cell.value) : "—"}
             onChange={(e) => setOverrides({ ...overrides, [k]: e.target.value })}
             className="input-rs col-span-2 font-mono text-[11px]" style={{ minHeight: 28 }} />
      <span className="col-span-1 inline-flex items-center justify-end gap-1">
        {cell.source === "user" && (
          <button data-testid={`ks-derived-${k}-reset`} onClick={() => onReset(k)}
            title="Reset to derived"
            className="text-[9px] font-mono uppercase text-[var(--rs-amber)] inline-flex items-center gap-0.5">
            <RotateCcw className="w-3 h-3" />
          </button>
        )}
        {cell.source === "user" && <span className="text-[8px] text-[var(--rs-amber)]">●</span>}
      </span>
    </div>
  );
}

function DerivedTable({ derived, overrides, setOverrides, onReset, units }) {
  const ctx = derived.wellContext || {};
  const dpHintVisible =
    derived.capacities.dpInternal.value == null
    && derived.capacities.dpInCasing.value == null
    && derived.capacities.dpHwdpInOpenHole.value == null
    && overrides.capDpInternal == null && overrides.capDpInCasing == null && overrides.capDpHwdpInOh == null;
  const hwdpHintVisible =
    derived.capacities.hwdpInternal.value == null && derived.capacities.hwdpInOpenHole.value == null
    && overrides.capHwdpInternal == null && overrides.capHwdpInOh == null;
  const dcHintVisible =
    derived.capacities.dcInternal.value == null
    && derived.capacities.dcInOpenHole.value == null
    && overrides.capDcInternal == null && overrides.capDcInOh == null;

  return (
    // Single column below `sm`. At 390px the two-column split left each
    // value input ~33px of usable text width — under five monospace
    // characters — so a depth like 12500 ft was silently clipped into a
    // plausible-looking wrong number. Every col-span-2 child inside this
    // grid is matched to col-span-1 at the same breakpoint, otherwise the
    // span would create an implicit second column and re-break the row.
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <SectionHeader title="Well / Depth" testid="ks-section-well-depth" />
      <ContextRow label="MD"             value={ctx.currentMd}  unit={units === "SI" ? "m" : "ft"} testid="ks-ctx-md" />
      <ContextRow label="TVD"            value={ctx.currentTvd} unit={units === "SI" ? "m" : "ft"} testid="ks-ctx-tvd" />
      <ContextRow label="Hole Size"      value={ctx.holeSize}   unit={units === "SI" ? "mm" : "in"} testid="ks-ctx-hole-size" />
      <ContextRow label="Casing ID"      value={ctx.casingId}   unit={units === "SI" ? "mm" : "in"} testid="ks-ctx-casing-id" />
      <ContextRow label="Casing Shoe MD" value={ctx.shoeMd}     unit={units === "SI" ? "m" : "ft"} testid="ks-ctx-shoe-md" />
      <ContextRow label="Casing Shoe TVD" value={ctx.shoeTvd}   unit={units === "SI" ? "m" : "ft"} testid="ks-ctx-shoe-tvd" />
      <ContextRow label="Mud Weight"     value={ctx.mudWeight}  unit={units === "SI" ? "kg/m³" : "ppg"} testid="ks-ctx-mud-weight" />

      <SectionHeader title="BOP" testid="ks-section-bop" />
      <ContextRow label="BOP Type"        value={ctx.bopType}               testid="ks-ctx-bop-type" />
      <ContextRow label="Bore Size"       value={ctx.bopNominalSize}        testid="ks-ctx-bop-bore" />
      <ContextRow label="Working Pressure" value={ctx.bopWorkingPressure}   unit="psi" testid="ks-ctx-bop-working-pressure" />
      <ContextRow label="Annular Working Pressure" value={ctx.bopAnnularWorkingPressure} unit="psi" testid="ks-ctx-bop-annular-pressure" />
      <ContextRow label="Ram Working Pressure"     value={ctx.bopRamWorkingPressure}     unit="psi" testid="ks-ctx-bop-ram-pressure" />
      {ctx.bopType == null && ctx.bopAnnularWorkingPressure == null && ctx.bopRamWorkingPressure == null && (
        <div data-testid="ks-derived-bop-hint"
             className="col-span-1 sm:col-span-2 text-[10.5px] text-[var(--rs-text-3)] italic mt-1 p-2 rounded border border-[var(--rs-border)]">
          BOP context needs a BOP selected on the Well Profile or the active
          Rig Data Sheet (Well Control → Surface BOP), bound to the active
          Rig Engineering Session.
        </div>
      )}

      <SectionHeader title="Drill Pipe" testid="ks-section-drill-pipe" />
      <DerivedRow label="DP Length" k="dp" cell={derived.lengths.dp}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <ContextRow label="DP OD" value={derived.drillPipe?.odIn} testid="ks-ctx-dp-od" />
      <ContextRow label="DP ID" value={derived.drillPipe?.idIn} testid="ks-ctx-dp-id" />
      <DerivedRow label="DP cap (internal)" k="capDpInternal" cell={derived.capacities.dpInternal}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="DP-csg ann cap" k="capDpInCasing" cell={derived.capacities.dpInCasing}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="DP/HWDP-OH ann cap" k="capDpHwdpInOh" cell={derived.capacities.dpHwdpInOpenHole}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      {dpHintVisible && (
        <div data-testid="ks-derived-dp-cap-hint"
             className="col-span-1 sm:col-span-2 text-[10.5px] text-[var(--rs-text-3)] italic mt-1 p-2 rounded border border-[var(--rs-border)]">
          DP cap (internal), DP-csg ann cap and DP/HWDP-OH ann cap need Drill
          Pipe OD/ID. Bind a Drill String with a Drill Pipe section to the
          active Rig Engineering Session (Engineering Setup → Rig Engineering),
          or set the well&apos;s active Drill String. A Drill Pipe component in the
          BHA profile is used only as a fallback.
        </div>
      )}

      <SectionHeader title="HWDP" testid="ks-section-hwdp" />
      <DerivedRow label="HWDP Length" k="hwdp" cell={derived.lengths.hwdp}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <ContextRow label="HWDP OD" value={derived.hwdpGeometry?.odIn} testid="ks-ctx-hwdp-od" />
      <ContextRow label="HWDP ID" value={derived.hwdpGeometry?.idIn} testid="ks-ctx-hwdp-id" />
      <DerivedRow label="HWDP cap (internal)" k="capHwdpInternal" cell={derived.capacities.hwdpInternal}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="HWDP/OH ann cap" k="capHwdpInOh" cell={derived.capacities.hwdpInOpenHole}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      {hwdpHintVisible && (
        <div data-testid="ks-derived-hwdp-cap-hint"
             className="col-span-1 sm:col-span-2 text-[10.5px] text-[var(--rs-text-3)] italic mt-1 p-2 rounded border border-[var(--rs-border)]">
          HWDP cap (internal) and HWDP/OH ann cap need an HWDP component with
          OD/ID entered on the bound BHA profile.
        </div>
      )}

      <SectionHeader title="Drill Collars" testid="ks-section-dc" />
      <DerivedRow label="DC Length" k="dc" cell={derived.lengths.dc}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <ContextRow label="DC OD" value={derived.dcGeometry?.odIn} testid="ks-ctx-dc-od" />
      <ContextRow label="DC ID" value={derived.dcGeometry?.idIn} testid="ks-ctx-dc-id" />
      <DerivedRow label="DC cap (internal)" k="capDcInternal" cell={derived.capacities.dcInternal}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="DC-OH ann cap" k="capDcInOh" cell={derived.capacities.dcInOpenHole}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      {dcHintVisible && (
        <div data-testid="ks-derived-dc-cap-hint"
             className="col-span-1 sm:col-span-2 text-[10.5px] text-[var(--rs-text-3)] italic mt-1 p-2 rounded border border-[var(--rs-border)]">
          DC cap (internal) and DC-OH ann cap need a Drill Collar component
          with OD/ID entered on the bound BHA profile.
        </div>
      )}

      <SectionHeader title="BHA" testid="ks-section-bha" />
      <DerivedRow label="BHA Length" k="bha" cell={derived.lengths.bha}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="Open Hole (DP/HWDP)" k="dpHwdpOpenHole" cell={derived.lengths.dpHwdpOpenHole}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="Open Hole (DC)" k="dcOpenHole" cell={derived.lengths.dcOpenHole}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
      <DerivedRow label="DP in Casing" k="dpInCasing" cell={derived.lengths.dpInCasing}
                  overrides={overrides} setOverrides={setOverrides} onReset={onReset} />
    </div>
  );
}

function KickInput({ label, field, value, onChange, testid }) {
  return (
    <label className="text-[11px] flex flex-col gap-1">
      <span className="text-[var(--rs-text-3)] uppercase font-mono">{label}</span>
      <input type="number" step="0.01" data-testid={testid} value={value ?? ""}
             onChange={(e) => onChange(field, e.target.value)}
             className="input-rs" style={{ minHeight: 36 }} />
    </label>
  );
}

function ResultsGrid({ o, units }) {
  if (!o) return <div className="text-[11px] text-[var(--rs-text-3)]">Fill in profiles + kick data to compute.</div>;
  const uP = units === "SI" ? "kPa" : "psi";
  const cells = [
    ["KMD",        o.killCalc.kmd,          units === "SI" ? "kg/m³" : "ppg"],
    ["KMG",        o.killCalc.kmg,          units === "SI" ? "kPa/m" : "psi/ft"],
    ["ICP",        o.killCalc.icp,          uP],
    ["FCP",        o.killCalc.fcp,          uP],
    ["K/100 stk",  o.killCalc.kPer100,      uP],
    ["MAASP_new",  o.additionalOutputs.maaspNew, uP],
    ["BHP",        o.additionalOutputs.formationPressure, uP],
    ["Frac Margin",o.additionalOutputs.fractureMargin, uP],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {cells.map(([k, c, u]) => (
        <div key={k} className="card-rs p-2" data-testid={`ks-result-${k.replace(/[^a-z]/gi,'').toLowerCase()}`}>
          <div className="text-[10px] font-mono uppercase text-[var(--rs-text-3)]">{k}</div>
          <div className="text-[14px] font-mono text-[var(--rs-amber)]">
            {c?.display ?? "—"} <span className="text-[10px] text-[var(--rs-text-3)]">{u}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepDownAndGraph({ o, units }) {
  if (!o || !o.killCalc.stepDown?.length) return <div className="text-[11px] text-[var(--rs-text-3)]">—</div>;
  const uP = units === "SI" ? "kPa" : "psi";
  return (
    <div className="text-[11px]">
      <div className="text-[var(--rs-text-3)] mb-1">
        {o.killCalc.stepDown.length} rows · ICP {o.killCalc.icp.display} → FCP {o.killCalc.fcp.display} {uP}.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 font-mono">
        {o.killCalc.stepDown.map((r, i) => (
          <div key={i} className="card-rs px-2 py-1 text-[10px]">
            {r.strokes.display} stk → {r.pressure.display} {uP}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportFooter({ session, composed }) {
  const pro = isProActive(getCachedEntitlement());
  const ready = composed?.ok;
  // Success confirmation, same pattern as ProfileEditor.jsx's saveFlash --
  // a Save action with no visible result otherwise looks like it silently
  // did nothing. Cleared on the next edit (session changes) so a stale
  // "saved" never sits above unsaved work.
  const [saveFlash, setSaveFlash] = useState(null); // { wellName, savedAt }
  useEffect(() => { setSaveFlash(null); }, [session]);
  const goPrint = () => {
    if (!ready) return;
    // The printed document is a created record: freeze the Drill Pipe
    // geometry on the handed-off copy (no-op when already frozen at creation).
    const frozen = freezeDrillPipeGeometry(JSON.parse(JSON.stringify(session)));
    sessionStorage.setItem(`rsc.killsheet.print.${session.id}`, JSON.stringify({ session: frozen, output: composed.output }));
    window.open(`/kill-sheet/print/${session.id}`, "_blank");
  };
  const handleSaveToLibrary = () => {
    if (!ready) return;
    const row = saveKillSheet(session, composed.output);
    setSaveFlash({ wellName: row.wellName, savedAt: row.savedAt });
  };
  return (
    <div className="mt-4">
      {saveFlash && (
        <div data-testid="ks-save-flash" role="status"
             className="card-rs p-3 mb-3"
             style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.05)" }}>
          <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
               style={{ color: "var(--rs-amber)" }}>
            Saved to library.
          </div>
          <div className="text-[11.5px] text-[var(--rs-text-2)]">
            {saveFlash.wellName || "(unnamed well)"}
            {saveFlash.savedAt ? ` · ${new Date(saveFlash.savedAt).toLocaleTimeString()}` : ""}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        {!pro ? (
          <div data-testid="ks-pro-gate" className="text-[12px] inline-flex items-center gap-2 px-3 py-2 rounded"
               style={{ background: "rgba(245,180,0,0.06)", border: "1px solid rgba(245,180,0,0.45)" }}>
            <Lock className="w-4 h-4 text-[var(--rs-amber)]" />
            <span><strong className="text-[var(--rs-amber)]">Pro feature</strong> — print &amp; save.{" "}
              <Link to="/pricing" className="underline">Upgrade</Link></span>
          </div>
        ) : (
          <>
            <button data-testid="ks-print" disabled={!ready} onClick={goPrint}
                    className="text-[13px] px-4 py-2 rounded font-medium disabled:opacity-30"
                    style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
              <Printer className="w-4 h-4 inline-block mr-1" /> Open printable IWCF kill sheet
            </button>
            <button data-testid="ks-save" disabled={!ready}
                    onClick={handleSaveToLibrary}
                    className="text-[12px] px-3 py-1.5 rounded border border-[var(--rs-border)] text-[var(--rs-text-2)] disabled:opacity-30">
              Save to Library
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <section className="card-rs p-3 mb-3" data-testid={`ks-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--rs-text-1)]">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// ---- Quick-Edit slide-over drawer ------------------------------------------
function QuickEditDrawer({ drawer, errors, onChange, onClose, onSave }) {
  const Form = FORMS[drawer.kind];
  return (
    <div data-testid="ks-quick-edit-drawer"
         className="fixed inset-0 z-40 flex"
         role="dialog" aria-modal="true">
      {/* scrim */}
      <button data-testid="ks-quick-edit-scrim" aria-label="Close drawer"
              onClick={onClose}
              className="flex-1"
              style={{ background: "rgba(0,0,0,0.55)" }} />
      {/* panel */}
      <aside className="h-full overflow-y-auto"
             style={{
               width: "min(560px, 100vw)",
               background: "var(--rs-bg-1, #0A0B0E)",
               borderLeft: "1px solid var(--rs-border)",
             }}>
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 py-3"
                style={{ background: "rgba(10,11,14,0.96)", borderBottom: "1px solid var(--rs-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]">
              Quick edit · v{drawer.version}
            </div>
            <h3 className="text-[14px] font-semibold text-[var(--rs-text-1)] truncate" style={{ maxWidth: 360 }}>
              {PROFILE_KIND_LABELS[drawer.kind].replace(/ Profiles$/, " Profile")} — {drawer.label || "(unnamed)"}
            </h3>
          </div>
          <button data-testid="ks-quick-edit-close" type="button" onClick={onClose}
                  className="p-1.5 rounded hover:text-[var(--rs-amber)] text-[var(--rs-text-2)]"
                  title="Close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-4 py-3">
          {errors.length > 0 && (
            <div data-testid="ks-quick-edit-errors"
                 className="card-rs p-2 mb-3"
                 style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
              <div className="text-[11px] font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1">
                Cannot save — please fix:
              </div>
              <ul className="list-disc list-inside text-[11.5px] text-[var(--rs-text-1)]">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          <Form value={drawer.draft} onChange={onChange} />
          <div className="flex items-center gap-2 mt-4">
            <button data-testid="ks-quick-edit-save" type="button" onClick={onSave}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                    style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
              <Save className="w-4 h-4" /> Save new version
            </button>
            <button data-testid="ks-quick-edit-cancel" type="button" onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
                    style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
          <p className="text-[10.5px] text-[var(--rs-text-3)] mt-3 leading-snug">
            Saving creates a new version. After save you can choose whether this
            kill sheet should re-bind to the new version or stay locked on the
            snapshot it was created with.
          </p>
        </div>
      </aside>
    </div>
  );
}

// ---- Re-bind confirmation prompt (option 4b) ------------------------------
function RebindPrompt({ prompt, onConfirm, onDismiss }) {
  return (
    <div data-testid="ks-rebind-prompt"
         className="fixed inset-0 z-50 flex items-center justify-center"
         style={{ background: "rgba(0,0,0,0.6)" }}
         role="dialog" aria-modal="true">
      <div className="card-rs max-w-md mx-4 p-4"
           style={{ background: "var(--rs-bg-1, #0A0B0E)" }}>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-1">
          Snapshot decision
        </div>
        <h3 className="text-[15px] font-semibold text-[var(--rs-text-1)] mb-2">
          Use v{prompt.version} in this kill sheet?
        </h3>
        <p className="text-[12px] text-[var(--rs-text-2)] leading-snug">
          <strong>{prompt.label}</strong> is now <code>v{prompt.version}</code>.
          This kill sheet is still bound to the previous snapshot.
          Re-bind to use the new data, or keep the original snapshot for
          historical integrity.
        </p>
        <div className="flex items-center gap-2 mt-4">
          <button data-testid="ks-rebind-confirm" type="button" onClick={onConfirm}
                  className="px-4 py-2 rounded-md text-[13px] font-medium"
                  style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
            Use v{prompt.version}
          </button>
          <button data-testid="ks-rebind-dismiss" type="button" onClick={onDismiss}
                  className="px-4 py-2 rounded-md text-[13px]"
                  style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
            Keep current snapshot
          </button>
        </div>
      </div>
    </div>
  );
}


// ---- Onboarding Strip (Phase 1.5) ----------------------------------------
// Calm progress strip rendered above the Profiles section while the user
// still has unbound profile kinds. Single "Create <next kind>" CTA — the
// user can also use the inline "+" on any PickerRow directly; the strip
// exists purely to make first-time progress visible.
//
// Hard requirements (per UX-1 review):
//   * Returns null when every canonical kind is bound (bound === 6).
//   * No animations, no flashing, no nags. Read-only badge + one CTA.
//   * Never blocks anything; the user can ignore it entirely.
function OnboardingStrip({ session, onCreateNext }) {
  const bound  = countBound(session);
  const total  = ONBOARDING_ORDER.length;
  const nextK  = nextUnboundKind(session);
  if (!nextK) return null;
  const label  = KIND_DISPLAY_LABEL[nextK] || nextK;
  return (
    <div data-testid="ks-onboarding-strip"
         className="card-rs p-2 mb-3 flex items-center justify-between gap-3"
         style={{ borderColor: "rgba(245,180,0,0.4)", background: "rgba(245,180,0,0.04)" }}>
      <div className="min-w-0">
        <div className="text-[9.5px] font-mono uppercase tracking-[0.25em] text-[var(--rs-amber)]">
          Setup
        </div>
        <div data-testid="ks-onboarding-count"
             className="text-[12px] font-semibold text-[var(--rs-text-1)] mt-0.5 truncate">
          {bound} of {total} profiles ready
        </div>
        <p className="text-[10.5px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
          Each profile populates the kill sheet automatically. You can also
          tap the <span className="font-mono">+</span> on any row.
        </p>
      </div>
      <button type="button"
              data-testid={`ks-onboarding-create-${nextK}`}
              onClick={() => onCreateNext && onCreateNext(nextK)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium whitespace-nowrap"
              style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
        <Plus className="w-4 h-4" /> Create {label}
      </button>
    </div>
  );
}

// ---- InlineProfileCreator (Phase 1 — D1) ---------------------------------
// Mounts the same FORMS[kind] component used by ProfileEditor and the
// QuickEditDrawer, inside the KS document flow. On Save:
//   1. VALIDATORS[kind] gates the payload (same gate as ProfileEditor).
//   2. createProfile(kind, payload) — identical storage call to ProfileEditor.
//   3. onSaved(newId) — parent KS calls attachProfile, same code path as
//      pickProfile, embedding the snapshot at session[kind].
// On Cancel: nothing is persisted; the local draft is dropped.
//
// Intentional non-goals:
//   * No multi-row simultaneous editing (parent enforces single-active).
//   * No persistence of the in-flight draft (Cancel == nothing happened).
//   * No navigation; the KS never unmounts.
function InlineProfileCreator({ id, kind, onCancel, onSaved }) {
  const Form = FORMS[kind];
  const [draft, setDraft] = useState(() => EMPTY_FACTORIES[kind]());
  const [errors, setErrors] = useState([]);
  const containerRef = useRef(null);
  // Move focus into the first input on mount so a thumb-tap on "+" lands
  // the user directly in the form (UX-1 review pain point #5).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const first = el.querySelector("input, select, textarea");
    if (first && typeof first.focus === "function") {
      try { first.focus({ preventScroll: false }); } catch { first.focus(); }
    }
  }, []);
  // Escape closes the accordion (consistent with QuickEditDrawer).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel && onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const handleSave = () => {
    const result = VALIDATORS[kind](draft);
    if (!result.ok) { setErrors(result.errors || []); return; }
    try {
      const row = createProfile(kind, draft);
      onSaved && onSaved(row.id);
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed"]);
    }
  };

  return (
    <div id={id}
         ref={containerRef}
         data-testid={`ks-inline-creator-${kind}`}
         className="mt-2 pt-2"
         style={{ borderTop: "1px dashed var(--rs-border)" }}
         role="region"
         aria-label={`Create new ${KIND_DISPLAY_LABEL[kind] || kind} profile`}>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.25em] text-[var(--rs-text-3)]">
            New profile
          </div>
          <div className="text-[12px] font-semibold text-[var(--rs-text-1)]">
            {KIND_DISPLAY_LABEL[kind] || kind}
          </div>
        </div>
      </div>
      {errors.length > 0 && (
        <div data-testid={`ks-inline-creator-errors-${kind}`}
             className="card-rs p-2 mb-2"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="text-[11px] font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1">
            Cannot save — please fix:
          </div>
          <ul className="list-disc list-inside text-[11.5px] text-[var(--rs-text-1)]">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      <Form value={draft} onChange={setDraft} />
      <div className="flex items-center gap-2 mt-3">
        <button data-testid={`ks-inline-creator-save-${kind}`}
                type="button" onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 38 }}>
          <Save className="w-4 h-4" /> Save profile
        </button>
        <button data-testid={`ks-inline-creator-cancel-${kind}`}
                type="button" onClick={onCancel}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
                style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 38 }}>
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>
      <p className="text-[10.5px] text-[var(--rs-text-3)] mt-2 leading-snug">
        Saving creates a v1 profile and binds it to this kill sheet
        automatically. Cancel discards the form — nothing is saved.
      </p>
    </div>
  );
}
